import React from "react";

/* Estimate table primitives — shared by the Tender Estimate and the Job
 * Template editor so a template's default lines read exactly like the
 * estimate they will become. Moved verbatim from TenderEstimate.jsx. */

const cls = (...a) => a.filter(Boolean).join(" ");

export const GROUP = {
  labour:   { bg: "#EEF3EE", fg: "#3A5A47" },
  material: { bg: "#F8F1DF", fg: "#7A6234" },
  plant:    { bg: "#F1ECF6", fg: "#5A4A78" },
  subby:    { bg: "#EBE9F4", fg: "#4A3F87" },
  total:    { bg: "#1A1A1A", fg: "#F1F1F1" }
};

export const TYPE_META = {
  library: { label: "LIBRARY", bg: "#EDE8DD", fg: "#6B5E3F", border: "#DCD2BD" },
  // LT-Estimate-UX-01 Section 3 — library line with any pricing cell diff
  // vs its recipe_snapshot. Preserves the "came from the library" provenance
  // while surfacing the edit clearly. Amber tokens match `--warning` in the
  // design system.
  library_edited: { label: "LIBRARY \u00b7 EDITED", bg: "#FDF3E1", fg: "#B45309", border: "#F2D9A6" },
  adhoc:   { label: "AD-HOC",  bg: "#FEF3E2", fg: "#B45309", border: "#F2DFB8" },
  subby:   { label: "SUBBY",   bg: "#E8E5F4", fg: "#4A3F87", border: "#D4CFEC" },
  prelim:  { label: "PRELIM",  bg: "#E5EEF7", fg: "#1F4E83", border: "#CDDDED" }
};

export function TypeBadge({ value, overridden = false }) {
  const isEdited = value === "library" && overridden;
  const effectiveKey = isEdited ? "library_edited" : (value || "adhoc");
  const meta = TYPE_META[effectiveKey] || TYPE_META.adhoc;
  return (
    <span
      title={isEdited
        ? "Line came from the library; one or more cells have been edited. Origin is fixed."
        : (value === "library"
            ? "Pulled from the Master Library (fixed provenance)"
            : "Typed manually on this tender (fixed provenance)")}
      data-testid={`type-badge-${value}${overridden ? "-edited" : ""}`}
      className="inline-flex items-center justify-center rounded font-mono max-w-full"
      style={{
        background: meta.bg, color: meta.fg, border: `1px solid ${meta.border}`,
        padding: "2px 6px", fontSize: 9.5, fontWeight: 600, letterSpacing: "0.06em",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "clip", lineHeight: 1.4,
      }}
    >
      {meta.label}
    </span>
  );
}

export function GroupTh({ children, group, colSpan = 1, rounded }) {
  const g = GROUP[group];
  return (
    <th colSpan={colSpan} className="text-center"
      style={{
        background: g.bg, color: g.fg,
        fontSize: 9.5, letterSpacing: "0.08em", fontWeight: 700,
        textTransform: "uppercase", padding: "6px 8px",
        borderTopLeftRadius: rounded === "l" || rounded === "both" ? 6 : 0,
        borderTopRightRadius: rounded === "r" || rounded === "both" ? 6 : 0
      }}>
      {children}
    </th>
  );
}

export function SubTh({ children, align = "left", group }) {
  const tint = GROUP[group]?.bg;
  return (
    <th className="col-head"
      style={{ textAlign: align, padding: "8px 8px",
        background: tint ? `${tint}55` : "transparent",
        borderTop: "1px solid #EDE8DD" }}>
      {children}
    </th>
  );
}

export function GCell({ children, align = "left", group, className = "", style = {} }) {
  const tint = GROUP[group]?.bg;
  return (
    <td className={cls("py-2", className)}
      style={{
        background: tint ? `${tint}26` : "transparent",
        textAlign: align,
        ...style
      }}>
      {children}
    </td>
  );
}
